import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Divider,
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';
import { loginRequest } from '../../config/msalConfig';

type LoginMode = 'enterEmail' | 'enterCode';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { requestEmailCode, loginWithEmailCode, error, clearError } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [mode, setMode] = useState<LoginMode>('enterEmail');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);

  const handleSsoLogin = async () => {
    setSsoLoading(true);
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('SSO login error:', err);
      setSsoLoading(false);
    }
  };

  const onSendCode = async () => {
    if (!otpEmail || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(otpEmail)) {
      setOtpError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setOtpError(null);

    try {
      await requestEmailCode(otpEmail);
      setMode('enterCode');
    } catch {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onVerifyCode = async () => {
    if (!otpCode || !/^\d{6}$/.test(otpCode)) {
      setOtpError('Please enter a valid 6-digit code');
      return;
    }

    setIsSubmitting(true);
    setOtpError(null);
    clearError();

    try {
      const user = await loginWithEmailCode(otpEmail, otpCode);
      if (user.role === 'admin') {
        navigate(ROUTES.ADMIN_DASHBOARD);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch (err: any) {
      setOtpError(err.message || 'Invalid or expired code');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onResendCode = async () => {
    setIsSubmitting(true);
    setOtpError(null);
    setOtpCode('');

    try {
      await requestEmailCode(otpEmail);
    } catch {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetToEmailEntry = () => {
    clearError();
    setOtpError(null);
    setOtpEmail('');
    setOtpCode('');
    setMode('enterEmail');
  };

  // --- ENTER EMAIL FOR CODE ---
  if (mode === 'enterEmail') {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
          Sign In
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We'll send a 6-digit code to your email address.
        </Typography>

        {otpError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {otpError}
          </Alert>
        )}

        <Box sx={{ textAlign: 'left' }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            margin="normal"
            value={otpEmail}
            onChange={(e) => setOtpEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSendCode(); }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={isSubmitting}
            onClick={onSendCode}
            sx={{ mt: 3, mb: 2 }}
          >
            {isSubmitting ? 'Sending...' : 'Send Code'}
          </Button>
        </Box>

        <Divider sx={{ my: 3 }}>or</Divider>

        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={handleSsoLogin}
          disabled={ssoLoading}
          sx={{
            borderColor: '#0078d4',
            color: '#0078d4',
            '&:hover': {
              borderColor: '#106ebe',
              backgroundColor: 'rgba(0, 120, 212, 0.04)',
            },
          }}
        >
          {ssoLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
        </Button>
      </Box>
    );
  }

  // --- ENTER 6-DIGIT CODE ---
  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
        Enter your code
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We sent a 6-digit code to <strong>{otpEmail}</strong>
      </Typography>

      {(otpError || error) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {otpError || error}
        </Alert>
      )}

      <Box sx={{ textAlign: 'center' }}>
        <TextField
          fullWidth
          placeholder="000000"
          value={otpCode}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
            setOtpCode(val);
          }}
          onKeyDown={(e) => { if (e.key === 'Enter' && otpCode.length === 6) onVerifyCode(); }}
          inputProps={{
            maxLength: 6,
            inputMode: 'numeric',
            autoComplete: 'one-time-code',
            style: {
              textAlign: 'center',
              fontSize: '32px',
              fontWeight: 700,
              letterSpacing: '8px',
              fontFamily: 'monospace',
            },
          }}
          sx={{ mb: 3 }}
        />

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={isSubmitting || otpCode.length !== 6}
          onClick={onVerifyCode}
          sx={{ mb: 2 }}
        >
          {isSubmitting ? 'Verifying...' : 'Verify'}
        </Button>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Link
            component="button"
            variant="body2"
            onClick={resetToEmailEntry}
            sx={{ cursor: 'pointer' }}
          >
            Back
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={onResendCode}
            sx={{ cursor: 'pointer' }}
          >
            Resend code
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
